<template>
  <div class="card mb-2" ref="card">
    <div class="card-header bg-primary text-white">
      <span class="font-weight-bold">Instrument Details</span>
    </div>
    <div class="card-body">
      <form @submit.prevent class="needs-validation" novalidate>
        <div v-if="serverError" class="alert alert-danger" role="alert">
          {{serverError}}
        </div>
         <div class="form-group row">
          <label for="agreementType" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Agreement Type
          </label>
          <div class="col-lg-8">
            <select v-validate="'required'"
                    data-vv-as="Agreement Type"
                    class="form-control"
                    name="agreementType"
                    v-model="instrument.agreementTypeId">
              <option value="" disabled>Select Agreement Type</option>
              <option v-for="option in agreementTypeOptions"
                      :key="option.id"
                      :value="option.id">
              {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('agreementType') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label class="col-lg-4 label-text">
            Instrument ID
          </label>
          <div class="col-lg-5">
            {{ instrument.id }}
          </div>
        </div>
        <div class="form-group row">
          <label for="instrumentType" class="col-lg-4 label-text">
            Instrument Type
          </label>
          <div class="loading col-lg-5" v-if="instrumentTypeLoading">
            Loading...
          </div>
          <div class="col-lg-8">
            <select v-if="!instrumentTypeLoading"
                    data-vv-as="Instrument Type"
                    class="form-control"
                    name="instrumentType"
                    v-model="instrument.typeId">
              <option :value="null">Select Instrument Type</option>
              <option v-for="option in instrumentTypeOptions"
                      :key="option.id"
                      :value="option.id">
              {{ `${option.name} (${option.code})` }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('instrumentType') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="instrumentStatus" class="col-lg-4 label-text">
            <span class="text-danger">*</span>Instrument Status
          </label>
          <div class="col-lg-8">
            <select v-validate="{
                      required : true
                    }"
                    data-vv-as="Instrument Status"
                    class="form-control"
                    name="instrumentStatus"
                    v-model="instrument.statusId">
              <option value="" disabled>Select Instrument Status</option>
              <option v-for="option in instrumentStatusOptions"
                      :key="option.id"
                      :value="option.id">
              {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('instrumentStatus') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="instrumentNumber" class="col-lg-4 label-text">
            Instrument Number
          </label>
          <div class="col-lg-8">
            <input data-vv-as="Instrument Number"
                   type="text"
                   class="form-control"
                   name="instrumentNumber"
                   v-model="instrument.instrumentNumber"
                   placeholder="Enter Instrument Number"
                   maxlength="18" />
            <small v-if="!errors.first('instrumentNumber')" class="form-text text-muted">
              Max 18 characters
            </small>
            <div class="invalid-feedback">{{ errors.first('instrumentNumber') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="locality" class="col-lg-4 label-text">
            Locality
          </label>
          <div class="loading col-lg-5" v-if="localityLoading">
            Loading...
          </div>
          <div class="col-md-8">
            <select v-if="!localityLoading"
                    data-vv-as="Locality"
                    class="form-control"
                    name="instrumentLocality"
                    v-model="instrument.localityId">
              <option :value="null">Select Locality</option>
              <option v-for="option in localityOptions"
                      :key="option.id"
                      :value="option.id">
              {{ option.name }}
              </option>
            </select>
            <div class="invalid-feedback">{{ errors.first('locality') }}</div>
          </div>
        </div>
        <div class="form-group row">
          <label for="deedBookPage" class="col-lg-4 label-text">
            Deed Book/Page
          </label>
          <div class="col-md-8">
            <input data-vv-as="Deed Book/Page"
                   type="text"
                   class="form-control"
                   v-validate="{ regex: /^(\d{1,6})\/(\d{1,6})$/ }"
                   name="deedBookPage"
                   v-model="instrument.deedBookPage"
                   placeholder="Enter Deed Book/Page"
                   maxlength="13" />
            <small v-if="!errors.first('deedBookPage')" class="form-text text-muted">
              ######/###### format
            </small>
            <div class="invalid-feedback">{{ errors.first('deedBookPage') }}</div>
          </div>
        </div>
        <StaffLeadAssignment
          :staffLead="instrument.staffLead"
          :staffLeadName="instrument.staffLeadName"
          :showSystemDefaultWarning="true"
          @update-staff-lead-selected="updateStaffLeadSelected"
        ></StaffLeadAssignment>
        <div class="form-group row">
          <label class="col-lg-4 label-text">
            Tags
          </label>
        <template v-if="tagTypeLoading">
          Loading Tag Types...
        </template>
        <div class="col-lg-8" v-if="!tagTypeLoading">
            <b-form-checkbox-group
              v-model="instrument.tagTypeIds"
              data-vv-as="Instrument Tag Types"
              name="instrumentTagTypes"
            >
              <b-form-checkbox
                v-for="tagTypeOption in tagTypeOptions"
                :key="tagTypeOption.id"
                :value="tagTypeOption.id"
                class="col-md-8 noselect">
                {{ tagTypeOption.name }}
              </b-form-checkbox>
            </b-form-checkbox-group>
            <div v-if="errors.first('instrumentTagTypes')"
              class="invalid-feedback d-block mb-2">
              {{ errors.first('instrumentTagTypes') }}
            </div>
          </div>
        </div>
        <div class="form-group row">
          <label for="instrumentNotes" class="col-lg-4 label-text">
            Instrument Notes
          </label>
          <div class="col-lg-8">
            <textarea data-vv-as="Notes"
                      type="textarea"
                      class="form-control"
                      name="notes"
                      v-model="instrument.notes"
                      placeholder="Enter Notes"
                      rows="3"
                      maxlength="1500" />
              <small v-if="!errors.first('notes')" class="form-text text-muted">
                Max 1500 characters
              </small>
              <div class="invalid-feedback">{{ errors.first('notes') }}</div>
            </div>
          </div>
           <div class="form-group row" v-if="instrument.legacyControlNumber">
          <label class="col-lg-4 label-text">
            Legacy Control Number(s)
          </label>
          <div class="col-lg-8">
            {{instrument.legacyControlNumber}}
          </div>
        </div>
        <div v-if="needExecutedEvent">
          <hr>
          <div class="label-text title-buffer">Instrument Event Required</div>
          <EventRequiredForm :editEvent="executedEvent"></EventRequiredForm>
        </div>
        <hr>
        <div class="form-group row">
          <label class="col-lg-4 label-text">
            Specific Terms
          </label>
        <template v-if="specificTermLoading">
          Loading Specific Terms...
        </template>
        <div class="col-lg-8" v-if="!specificTermLoading">
            <b-form-checkbox-group
              v-model="instrument.specificTermIds"
              data-vv-as="Instrument Specific Terms"
              name="instrumentSpecificTerms"
            >
              <b-form-checkbox
                v-for="specificTermOption in specificTermOptions"
                :key="specificTermOption.id"
                :value="specificTermOption.id"
                class="col-md-8 noselect">
                {{ specificTermOption.name }}
              </b-form-checkbox>
            </b-form-checkbox-group>
            <div class="row unique-term-row">
              <b-form-checkbox
                 name="uniqueTermsCheckbox"
                 key=0
                 v-model="uniqueTermsChecked"
                 @change="uniqueTermsChanged"
                 class="col-md-4 noselect"
                 >
                 Unique Term(s)
              </b-form-checkbox>
          </div>
          <div class="form-group row unique-top-row">
            <template v-if="instrument.uniqueTerms && instrument.uniqueTerms.length > 0">
                <div class="col-lg-11">
                  <input v-model="instrument.uniqueTerms[0].description"
                    class="form-control"
                    maxlength="100"
                    v-validate="'required'"
                    name="uniqueTerm"
                    data-vv-as="Unique Term"
                  />
                  <small v-if="!errors.first('uniqueTerm')" class="form-text text-muted">
                    Max 100 characters
                  </small>
                  <div class="invalid-feedback">{{ errors.first('uniqueTerm') }}</div>
                </div>
                <div v-if="instrument.uniqueTerms.length > 1"
                  class="col-lg-1 pl-md-0 d-flex flex-row-reverse
                  flex-md-column justify-content-between justify-content-md-start
                  delete-vertical-offset">
                  <div class="d-flex justify-content-end">
                    <b-button variant="danger" size="sm" class="mr-2 mb-2"
                      @click='removeUniqueTerm(index + 1)'>
                      <font-awesome-icon icon="trash-alt" class="text-white" fixed-width />
                    </b-button>
                  </div>
                </div>
              </template>
            </div>
            <div v-if="instrument.uniqueTerms && instrument.uniqueTerms.length > 1">
              <div v-for="(uniqueTerm, index) in instrument.uniqueTerms.slice(1)" :key="index">
                <div class="form-group row">
                  <div class="col-lg-11">
                    <input v-model="uniqueTerm.description"
                      class="form-control"
                      maxlength="100"
                      v-validate="'required'"
                      :name="`uniqueTerm-${index}`"
                      data-vv-as="Unique Term"
                    />
                    <small v-if="!errors.first(`uniqueTerm-${index}`)" class="form-text text-muted">
                      Max 100 characters
                    </small>
                    <div class="invalid-feedback">{{ errors.first(`uniqueTerm-${index}`) }}</div>
                  </div>
                  <div class="col-lg-1 pl-md-0 d-flex flex-row-reverse
                     flex-md-column justify-content-between justify-content-md-start
                     delete-vertical-offset">
                      <div class="d-flex justify-content-end">
                        <b-button variant="danger" size="sm" class="mr-2 mb-2"
                          @click='removeUniqueTerm(index + 1)'>
                          <font-awesome-icon icon="trash-alt" class="text-white" fixed-width />
                        </b-button>
                      </div>
                  </div>
                </div>
              </div>
            </div>
            <template v-if="instrument.uniqueTerms.length > 0 && instrument.uniqueTerms.length < 5">
              <div class="row">
                <a class="form-action-text text-secondary white-space-nowrap"
                   @click="addUniqueTerm()">
                  + Add Unique Term
                </a>
              </div>
            </template>
            <div v-if="errors.first('instrumentSpecificTerms')"
              class="invalid-feedback d-block mb-2">
              {{ errors.first('instrumentSpecificTerms') }}
            </div>
          </div>
        </div>
        <CancelModal
               modalId="cancelConfirmation"
               ref='cancelModal'
               @cancelled="cancelConfirmed">
        </CancelModal>
      </form>
    </div>
    <div class="card-footer right-align-content">
      <b-button :disabled="submitting"
        variant="danger"
        class="button-row-spacing"
        v-b-modal="'cancelConfirmation'"
      >
        Cancel
      </b-button>
      <b-button @click="submitWrapper(instrument, executedEvent)" :disabled="submitting">
        Save
      </b-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import EventRequiredForm from '@/components/event/EventRequiredForm.vue';
import CollapseControl from '@/mixins/CollapseControl';
import FormSubmit from '@/mixins/FormSubmit';
import { LookupModules, EntityModules } from '@/store/moduleConfig';
import StaffLeadAssignment from '@/components/common/StaffLeadAssignment.vue';
import CancelModal from '../CancelModal.vue';
import EventDateHelper from '../../mixins/EventDateHelper';

export default {
  name: 'EditInstrumentForm',
  mixins: [CollapseControl, EventDateHelper, FormSubmit],
  data() {
    return {
      uniqueTermsChecked: false,
      instrument: {
        statusId: null,
        uniqueTerms: [],
        staffLead: null,
        staffLeadName: null,
      },
      initialStatusId: null,
      executedEvent: {
        eventTypeId: 9,
        eventTypeName: 'Executed',
        notes: null,
        date: null,
      },
    };
  },
  computed: {
    ...mapState('Instrument', {
      currentInstrument: state => state.currentInstrument,
      serverError: state => state.serverError,
      submitting: state => state.submitting,
    }),
    ...mapState('User', {
      activeUsers: state => state.activeUsers,
      loadingUsers: state => state.loading,
      user: state => state.user,
    }),
    ...mapState(EntityModules.INSTRUMENT_REVIEW.namespace, {
      reviews: state => state.entities,
    }),
    ...mapState(LookupModules.LOCALITIES.namespace, {
      localityLoading: state => state.loading,
    }),
    ...mapState(LookupModules.INSTRUMENT_TYPES.namespace, {
      instrumentTypeLoading: state => state.loading,
    }),
    ...mapState(LookupModules.SPECIFIC_TERMS.namespace, {
      specificTermLoading: state => state.loading,
    }),
    ...mapState(LookupModules.TAG_TYPES.namespace, {
      tagTypeLoading: state => state.loading,
    }),
    ...mapGetters(LookupModules.LOCALITIES.namespace, {
      localityOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.AGREEMENT_TYPES.namespace, {
      agreementTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.INSTRUMENT_STATUSES.namespace, {
      instrumentStatusOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.INSTRUMENT_TYPES.namespace, {
      instrumentTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.TAG_TYPES.namespace, {
      tagTypeOptions: 'getActiveOptions',
    }),
    ...mapGetters(LookupModules.SPECIFIC_TERMS.namespace, {
      specificTermOptions: 'getActiveOptions',
    }),
    ...mapGetters('User', {
      getCurrentUserFromActiveUsers: 'getCurrentUserFromActiveUsers',
    }),
    needExecutedEvent() {
      return this.instrument.statusId === 2
        && this.instrument.statusId !== this.initialStatusId;
    },
  },
  components: {
    CancelModal,
    EventRequiredForm,
    StaffLeadAssignment,
  },
  mounted() {
    this.initialStatusId = this.currentInstrument.statusId;
    Object.assign(this.instrument, this.currentInstrument);

    this.instrument.uniqueTerms = this.currentInstrument.uniqueTerms
      .map(ut => ({ ...ut }))
      .sort(this.uniqueTermsSort);
    if (this.instrument.uniqueTerms.length > 0) {
      this.uniqueTermsChecked = true;
    }
    this.$store.dispatch('User/fetchActiveUsers')
      .then(() => {
        if (!this.instrument.staffLead) {
          this.instrument.staffLeadSelected = this.getCurrentUserFromActiveUsers;
        } else {
          const staffLeadUser = this.activeUsers
            .filter(u => u.id === this.instrument.staffLead);
          this.instrument.staffLeadSelected = staffLeadUser ? staffLeadUser[0] : null;
        }
      });
  },
  methods: {
    cancelConfirmed() {
      this.$store.dispatch('Instrument/stopEditing');
      this.stopEditing();
    },
    submitWrapper(instrument, executedEvent) {
      const submittedInstrument = {};
      Object.assign(submittedInstrument, instrument);
      if (submittedInstrument.statusId === 2 && this.initialStatusId !== 2) {
        submittedInstrument.events = [executedEvent];
      }
      if (instrument.staffLeadSelected) {
        submittedInstrument.staffLead = instrument.staffLeadSelected.id;
        submittedInstrument.staffLeadName = instrument.staffLeadSelected.name;
      }
      this.onSubmit(
        submittedInstrument,
        'Instrument',
        null,
        this.cleanUpState,
      );
    },
    cleanUpState() {
      this.stopEditing();
      this.$store.dispatch(`${EntityModules.INSTRUMENT_EVENT.namespace}/fetchEntities`, {
        parentType: EntityModules.INSTRUMENT_EVENT.parentType,
        parentId: this.instrument.id,
        entityType: EntityModules.INSTRUMENT_EVENT.entityType,
      });
    },
    uniqueTermsChanged() {
      if (this.uniqueTermsChecked) {
        this.instrument.uniqueTerms.push({ id: null, description: '' });
      } else {
        this.instrument.uniqueTerms.splice(0, this.instrument.uniqueTerms.length);
      }
    },
    addUniqueTerm() {
      this.instrument.uniqueTerms.push({ id: null, description: '' });
    },
    removeUniqueTerm(index) {
      this.instrument.uniqueTerms.splice(index, 1);
    },
    uniqueTermsSort(a, b) {
      return a.id - b.id;
    },
    updateStaffLeadSelected(newStaffLead) {
      this.instrument.staffLeadSelected = newStaffLead;
    },
    stopEditing() {
      this.$store.dispatch('Common/stopEditMode', 'Instrument');
      this.$refs.card.scrollIntoView();
    },
  },
};
</script>
